import { supabase } from '../lib/supabase';

/**
 * Company Settings Service
 * Manages company branding configuration including name, logo, and watermark settings
 */

/**
 * Get current company branding settings
 * @returns {Promise<Object>} Company branding data
 */
export async function getCompanyBranding() {
  try {
    const { data, error } = await supabase
      ?.from('company_branding')
      ?.select('*')
      ?.single();

    if (error) throw error;

    // Convert snake_case to camelCase
    return {
      id: data?.id,
      companyName: data?.company_name,
      logoUrl: data?.logo_url,
      logoFilePath: data?.logo_file_path,
      watermarkEnabled: data?.watermark_enabled,
      watermarkOpacity: data?.watermark_opacity,
      watermarkPosition: data?.watermark_position,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  } catch (error) {
    console.error('Error fetching company branding:', error);
    throw new Error('فشل في تحميل إعدادات العلامة التجارية');
  }
}

/**
 * Update company branding settings
 * @param {Object} brandingData - Updated branding data
 * @returns {Promise<Object>} Updated branding data
 */
export async function updateCompanyBranding(brandingData) {
  try {
    // Convert camelCase to snake_case
    const updateData = {
      company_name: brandingData?.companyName,
      logo_url: brandingData?.logoUrl,
      logo_file_path: brandingData?.logoFilePath,
      watermark_enabled: brandingData?.watermarkEnabled,
      watermark_opacity: brandingData?.watermarkOpacity,
      watermark_position: brandingData?.watermarkPosition,
      updated_at: new Date()?.toISOString()
    };

    // Remove undefined values
    Object.keys(updateData)?.forEach(key => {
      if (updateData?.[key] === undefined) {
        delete updateData?.[key];
      }
    });

    const { data, error } = await supabase
      ?.from('company_branding')
      ?.update(updateData)
      ?.eq('id', brandingData?.id)
      ?.select()
      ?.single();

    if (error) throw error;

    // Convert snake_case to camelCase
    return {
      id: data?.id,
      companyName: data?.company_name,
      logoUrl: data?.logo_url,
      logoFilePath: data?.logo_file_path,
      watermarkEnabled: data?.watermark_enabled,
      watermarkOpacity: data?.watermark_opacity,
      watermarkPosition: data?.watermark_position,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  } catch (error) {
    console.error('Error updating company branding:', error);
    throw new Error('فشل في تحديث إعدادات العلامة التجارية');
  }
}

/**
 * Upload company logo to Supabase Storage
 * @param {File} file - Logo file to upload
 * @returns {Promise<Object>} Upload result with public URL
 */
export async function uploadCompanyLogo(file) {
  try {
    if (!file) {
      throw new Error('لم يتم تحديد ملف');
    }

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes?.includes(file?.type)) {
      throw new Error('نوع الملف غير مدعوم. يرجى استخدام PNG أو JPG أو SVG أو WEBP');
    }

    // Validate file size (2MB max)
    if (file?.size > 2097152) {
      throw new Error('حجم الملف كبير جداً. الحد الأقصى 2 ميجابايت');
    }

    // Generate unique filename
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `company-logo-${Date.now()}.${fileExt}`;
    const filePath = `logos/${fileName}`;

    // Upload to Supabase Storage
    const { data: uploadData, error: uploadError } = await supabase
      ?.storage
      ?.from('company-logos')
      ?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) throw uploadError;

    // Get public URL
    const { data: urlData } = supabase
      ?.storage
      ?.from('company-logos')
      ?.getPublicUrl(filePath);

    return {
      success: true,
      filePath: uploadData?.path,
      publicUrl: urlData?.publicUrl
    };
  } catch (error) {
    console.error('Error uploading company logo:', error);
    throw new Error(error?.message || 'فشل في رفع الشعار');
  }
}

/**
 * Delete company logo from Supabase Storage
 * @param {string} filePath - File path in storage
 * @returns {Promise<Object>} Deletion result
 */
export async function deleteCompanyLogo(filePath) {
  try {
    if (!filePath) {
      throw new Error('مسار الملف غير محدد');
    }

    const { error } = await supabase
      ?.storage
      ?.from('company-logos')
      ?.remove([filePath]);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Error deleting company logo:', error);
    throw new Error('فشل في حذف الشعار');
  }
}

export default {
  getCompanyBranding,
  updateCompanyBranding,
  uploadCompanyLogo,
  deleteCompanyLogo
};